import React, { useState } from 'react';
import styled from 'styled-components';
import { CampaignChannel, CampaignProcessStatus, CampaignRecipientStatusRow } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import StyledTable from '../../../components/StyledTable';
import { formatCampaignDateTime } from '../campaignsUtils';

const PAGE_SIZE = 20;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-body);
`;

const Pagination = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    font-size: 0.9rem;
    color: var(--text-muted);
`;

const statusDotColor = (status: CampaignProcessStatus): string => {
    switch (status) {
        case CampaignProcessStatus.Done: return 'var(--success-color)';
        case CampaignProcessStatus.Error: return 'var(--danger-color)';
        case CampaignProcessStatus.Sending: return 'var(--warning-color)';
        default: return 'var(--text-muted)';
    }
};

const getStatusKey = (status: CampaignProcessStatus): TK => {
    switch (status) {
        case CampaignProcessStatus.Done: return TK.finished;
        case CampaignProcessStatus.Error: return TK.error;
        case CampaignProcessStatus.Sending: return TK.sending;
        case CampaignProcessStatus.Created: return TK.created;
        case CampaignProcessStatus.Cancelled: return TK.canceled;
        default: return TK.unknown;
    }
};

interface CampaignRecipientStatusTableProps {
    rows: CampaignRecipientStatusRow[];
    channel?: CampaignChannel;
}

export const CampaignRecipientStatusTable: React.FC<CampaignRecipientStatusTableProps> = ({ rows, channel }) => {
    const t = useTranslations();
    const [page, setPage] = useState(0);

    if (!rows.length) return null;

    const totalPages = Math.ceil(rows.length / PAGE_SIZE);
    const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <Section>
            <SectionTitle>{t(TK.recipients)}</SectionTitle>
            <StyledTable scrollable hover size="sm">
                <thead>
                    <tr>
                        <th>{channel === CampaignChannel.WhatsApp ? t(TK.phoneNumber) : t(TK.email)}</th>
                        <th>{t(TK.status)}</th>
                        <th>{t(TK.delivered)}</th>
                        <th>{t(TK.opened)}</th>
                        <th>{t(TK.clicked)}</th>
                        <th>{t(TK.campaignAnalyticsRead)}</th>
                        <th>{t(TK.reason)}</th>
                    </tr>
                </thead>
                <tbody>
                    {pageRows.map((row, i) => (
                        <tr key={i}>
                            <td>
                                <div>{row.clientName ?? row.email}</div>
                                {row.clientName && (
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{row.email}</div>
                                )}
                            </td>
                            <td>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    marginRight: 'var(--spacer-XXS)',
                                    backgroundColor: statusDotColor(row.status),
                                }} />
                                {t(getStatusKey(row.status))}
                            </td>
                            <td>{row.deliveredAt ? formatCampaignDateTime(row.deliveredAt) : '—'}</td>
                            <td>{row.openedAt ? formatCampaignDateTime(row.openedAt) : '—'}</td>
                            <td>{row.clickedAt ? formatCampaignDateTime(row.clickedAt) : '—'}</td>
                            <td>{row.readAt ? formatCampaignDateTime(row.readAt) : '—'}</td>
                            <td>{row.failureReason ?? '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </StyledTable>
            {totalPages > 1 && (
                <Pagination>
                    <button
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        style={{ background: 'none', border: 'none', cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? 'var(--text-muted)' : 'var(--appBlue)', fontSize: '1.2rem', padding: '0 var(--spacer-XXS)' }}
                    >‹</button>
                    <span>{page + 1} / {totalPages}</span>
                    <button
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        style={{ background: 'none', border: 'none', cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: page >= totalPages - 1 ? 'var(--text-muted)' : 'var(--appBlue)', fontSize: '1.2rem', padding: '0 var(--spacer-XXS)' }}
                    >›</button>
                </Pagination>
            )}
        </Section>
    );
};
